/**
 * Type-safe environment variables
 *
 * All environment variables are managed through SST:
 * - Secrets are stored using `sst secret set` (see README_SECRETS.md)
 * - Configuration is defined in infra/web.ts
 * - Variables are type-safe through sst-env.d.ts
 */

import { z } from 'zod';

// Check if we have a malformed SST output (contains "Output<T>")
const cleanEnvValue = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  if (value.includes('Output<T>') || value.includes('[toString]')) {
    // This is an unresolved SST Output, ignore it
    return undefined;
  }
  return value;
};

// Define the schema for environment variables
const envSchema = z.object({
  // WorkOS Configuration
  VITE_WORKOS_CLIENT_ID: z.string().min(1, 'WorkOS Client ID is required'),
  VITE_WORKOS_REDIRECT_URI: z.string().url('WorkOS Redirect URI must be a valid URL').optional(),
  VITE_WORKOS_API_KEY: z.string().optional(),
  VITE_WORKOS_DOMAIN: z.string().optional(),

  // API Configuration
  VITE_API_URL: z.string().url('API URL must be a valid URL').optional(),

  // App Configuration
  VITE_APP_URL: z.string().url('App URL must be a valid URL').optional(),
  VITE_APP_NAME: z.string().default('SendGuard'),

  // Vite environment flags
  DEV: z.boolean().optional().default(false),
  PROD: z.boolean().optional().default(false),
  MODE: z.enum(['development', 'production', 'test']).optional().default('development'),

  // SST environment flag
  SST: z.boolean().optional(),
});

// Clean environment variables before parsing
const cleanedEnv = {
  ...import.meta.env,
  VITE_WORKOS_REDIRECT_URI: cleanEnvValue(import.meta.env.VITE_WORKOS_REDIRECT_URI),
  VITE_API_URL: cleanEnvValue(import.meta.env.VITE_API_URL),
  VITE_APP_URL: cleanEnvValue(import.meta.env.VITE_APP_URL),
};

// Parse environment variables with validation
const parsedEnv = envSchema.parse(cleanedEnv);

// Export structured environment variables with fallbacks for local development
const apiUrl = parsedEnv.VITE_API_URL || 'http://localhost:3000';

export const env = {
  // WorkOS
  workos: {
    clientId: parsedEnv.VITE_WORKOS_CLIENT_ID,
    apiKey: parsedEnv.VITE_WORKOS_API_KEY,
    redirectUri: parsedEnv.VITE_WORKOS_REDIRECT_URI || `${apiUrl}/auth/callback`,
    domain: parsedEnv.VITE_WORKOS_DOMAIN,
  },

  // API
  api: {
    url: apiUrl,
  },

  // App
  app: {
    url: parsedEnv.VITE_APP_URL || 'http://localhost:3000',
    name: parsedEnv.VITE_APP_NAME,
  },

  // Environment
  isDevelopment: parsedEnv.DEV ?? false,
  isProduction: parsedEnv.PROD ?? false,
  isSST: parsedEnv.SST ?? false,
  mode: parsedEnv.MODE,
} as const;

// Export type for the environment
export type Env = typeof env;

// Type-safe helper to check environment
export function isDevEnvironment(): boolean {
  return env.isDevelopment;
}

export function isProdEnvironment(): boolean {
  return env.isProduction;
}

// Validate that we're running in the expected environment
export function validateEnv(): void {
  // SST ensures all required environment variables are set
  // This function can be used for additional runtime checks if needed
  if (!env.workos.clientId) {
    console.warn('WorkOS Client ID not configured - authentication may not work');
  }
}
